import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/cores';
import * as empresasApi from '../../api/empresas';

function Modal({ item, empresas, emGlobal, onClose, onSaved }) {
  const editando = !!item?.id_cor;
  const [nome, setNome] = useState(item?.nome || '');
  const [hex, setHex] = useState(item?.codigo_hex || '#000000');
  const [idEmpresa, setIdEmpresa] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const precisaEmpresa = !editando && emGlobal;
  const empresaOpts = empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }));

  const salvar = async (e) => {
    e.preventDefault();
    if (precisaEmpresa && !idEmpresa) {
      setErro('Selecione a empresa dona desta cor.');
      return;
    }
    setErro('');
    setLoading(true);
    try {
      const payload = { nome, codigo_hex: hex, ...(precisaEmpresa ? { id_empresa_operadora: idEmpresa } : {}) };
      if (editando) await api.atualizar(item.id_cor, payload);
      else await api.criar(payload);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Cor' : 'Nova Cor'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}

              {precisaEmpresa && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Empresa *</label>
                  <SearchableSelect options={empresaOpts} value={idEmpresa}
                    onChange={setIdEmpresa} placeholder="Selecione a empresa dona desta cor..." required />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Você está no modo global — escolha para qual empresa esta cor será cadastrada.
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} required autoFocus />
              </div>
              <div>
                <label className="form-label fw-semibold">Cor</label>
                <input type="color" className="form-control form-control-color" value={hex} onChange={e => setHex(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Cores() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);

  const carregar = useCallback(() => {
    setLoading(true);
    api.listar().then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';

  const filtrados = items.filter(i => {
    const matchBusca = i.nome.toLowerCase().includes(busca.toLowerCase());
    const matchEmpresa = emGlobal && idEmpresaFiltro ? i.id_empresa_operadora === idEmpresaFiltro : true;
    return matchBusca && matchEmpresa;
  });
  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  const empresaFiltroOpts = [{ value: '', label: 'Todas as empresas' },
    ...empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }))];

  return (
    <div>
      <PageHeader titulo="Cores" subtitulo={`${items.length} cores`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Nova Cor</button>
      </PageHeader>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            {empresaFiltroOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <Th campo="nome">Nome</Th>
                <th>Amostra</th>
                {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 && (
                <tr><td colSpan={emGlobal && !idEmpresaFiltro ? 4 : 3} className="text-center text-muted py-4">Nenhuma cor cadastrada.</td></tr>
              )}
              {paginados.map(c => (
                <tr key={c.id_cor}>
                  <td className="fw-semibold">{c.nome}</td>
                  <td>
                    {c.codigo_hex && (
                      <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, background: c.codigo_hex, border: '1px solid #ddd' }} />
                    )}
                  </td>
                  {emGlobal && !idEmpresaFiltro && <td>{empresaNome(c.id_empresa_operadora)}</td>}
                  <td>
                    <button className="btn btn-sm btn-outline-primary py-0 px-2"
                      onClick={() => setModal(c)}>Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Paginacao />

      {modal !== null && (
        <Modal item={modal} empresas={empresas} emGlobal={emGlobal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); carregar(); }} />
      )}
    </div>
  );
}
